import { useFormContext } from 'react-hook-form';
import PhotoUploader from '../../../components/forms/PhotoUploader';
import { usePhotoUpload } from '../../../hooks/usePhotoUpload';
import { Button } from '../../../components/ui';

/**
 * Step 1: Photos
 *
 * Upload 1-8 photos for the raffle item.
 * First photo becomes the cover image.
 */
export default function StepPhotos({ onNext, onBack, isFirstStep }) {
  const { watch, setValue, formState: { errors } } = useFormContext();
  const photos = watch('photos');
  const { uploadPhoto } = usePhotoUpload();

  const hasPhotos = photos && photos.length > 0;
  const photoError = errors.photos?.message;

  function handlePhotosChange(newPhotos) {
    setValue('photos', newPhotos, { shouldValidate: true });
  }

  function handleContinue() {
    if (hasPhotos) {
      onNext();
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-display text-cream mb-2">
          Add Photos
        </h1>
        <p className="text-cream/60">
          Upload 1-8 photos of your item. The first photo will be the cover image.
        </p>
      </div>

      {/* Photo Uploader */}
      <div className="mb-8">
        <PhotoUploader
          photos={photos}
          onChange={handlePhotosChange}
          onUpload={uploadPhoto}
          maxPhotos={8}
          error={photoError}
        />
      </div>

      {/* Tips */}
      <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-5 mb-8">
        <h3 className="font-semibold text-cream mb-3 flex items-center gap-2">
          <span className="text-lg">💡</span>
          Photo Tips
        </h3>
        <ul className="space-y-2 text-cream/60 text-sm">
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Use good lighting - natural light works best
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Show the item from multiple angles
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Include close-ups of any damage or wear
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Keep backgrounds clean and uncluttered
          </li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isFirstStep}
          className={isFirstStep ? 'invisible' : ''}
        >
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!hasPhotos}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
